import { toast } from 'vue-sonner';

/**
 * Subscribes the currently-authenticated user to their private Echo channel and surfaces
 * staff-assignment lifecycle events (CU-86d2dcc7y) as toasts + diary cache invalidations.
 *
 * Three notification types ride on the same channel:
 *
 * - `staff-assignment-pending` — fired by {@link App\\Notifications\\StaffAssignmentPendingNotification}
 *   when this user is freshly assigned to a task. Prompts them to accept or decline.
 *
 * - `staff-assignment-auto-accepted` — fired by
 *   {@link App\\Notifications\\StaffAssignmentAutoAcceptedNotification} when this user is freshly
 *   assigned but the company default auto-accepts the assignment. Nothing to action, but the
 *   diary must still refresh so the task appears without a manual reload.
 *
 * - `staff-assignment-status-changed` — fired by
 *   {@link App\\Notifications\\StaffAssignmentStatusChangedNotification} when someone else
 *   (an operator) accepts or declines on this user's behalf. Self-actions are intentionally
 *   silent server-side, so this user never gets a toast for an action they themselves took.
 *
 * Mounted at the diary-shell level — single subscription per CRM session. Tenant-wide ops
 * fan-out continues to flow through the existing `tenant.{tenantId}` `FixListUpdatedEvent`
 * pipe; this composable only handles the per-user surface.
 */

interface UseStaffAssignmentRealtimeOptions {
    /**
     * Pinia auth store exposing the current user. Must expose either `user.value.id` or
     * a `getUserId` getter — the composable reads via `userStore.user?.id` first, then
     * falls back to `userStore.getUserId` if that's the shape this tenancy uses.
     * Also read for `tenantSlug`, which gates the subscription (see setupEchoListener).
     */
    userStore: any;
    /**
     * Pinia diary store. Used to invalidate the diaries TanStack Query cache on every
     * inbound assignment event so the diary refreshes within Pusher RTT.
     */
    diaryStore: any;
}

/**
 * The App-level instance, so the user store can re-establish this subscription.
 * `setTenant()` calls `window.echo.leaveAllChannels()` on every tenant resolution,
 * which drops this listener along with everything else; the store re-runs setup
 * afterwards (as it already does for the notification / job-count listeners).
 */
let activeListener: { setupEchoListener: () => void } | null = null;

export function resubscribeStaffAssignmentRealtime(): void {
    activeListener?.setupEchoListener();
}

export function useStaffAssignmentRealtime(options: UseStaffAssignmentRealtimeOptions) {
    const { userStore, diaryStore } = options;

    const echoChannelName = ref<string | null>(null);

    const currentUserId = (): number | null => {
        const id = userStore?.user?.id ?? userStore?.getUserId ?? null;
        return id ? Number(id) : null;
    };

    const currentTenantSlug = (): string | null => userStore?.tenantSlug ?? null;

    // Notifications come in via the framework-broadcast event name; payload is the array
    // returned by `toBroadcast()` on the PHP side. We match on `payload.type` so a single
    // listener handles both notification classes.
    const broadcastEventName = '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated';

    const handleInbound = (payload: any): void => {
        if (!payload || typeof payload !== 'object') {
            return;
        }

        const type = payload.type;
        const message = payload.message || 'Task assignment updated';

        if (type === 'staff-assignment-pending') {
            toast(message, {
                description: 'Open the diary to accept or decline.',
            });
        } else if (type === 'staff-assignment-auto-accepted') {
            // Company default is `accepted`: nothing for the assignee to action, but the diary
            // still needs to refresh so the new task shows up without a manual reload.
            toast(message);
        } else if (type === 'staff-assignment-status-changed') {
            if (payload.status === 'declined') {
                toast.error(message);
            } else {
                toast.success(message);
            }
        } else {
            // Unknown notification type — silently ignore (don't toast unrelated user
            // notifications that ride on the same channel).
            return;
        }

        // Refresh the diary so the avatar borders / fix-list pill / runsheet ticks
        // reflect the new state without a manual page reload.
        if (typeof diaryStore?.invalidateDiaries === 'function') {
            diaryStore.invalidateDiaries();
        }
    };

    // Stable reference so cleanup can unbind exactly this handler. The notifications store
    // listens for the same event on the same channel (`startListener()` → `.notification()`),
    // and an event-wide `stopListening(event)` would take its binding down with ours.
    const onBroadcastNotification = (event: any): void => handleInbound(event);

    const cleanupEchoListener = (): void => {
        if (echoChannelName.value && window.echo) {
            try {
                const channel = window.echo.private(echoChannelName.value);
                channel.stopListening(broadcastEventName, onBroadcastNotification);
            } catch (error) {
                console.error('Error stopping Echo listener in useStaffAssignmentRealtime:', error);
            }
        }
        echoChannelName.value = null;
    };

    const setupEchoListener = (): void => {
        const userId = currentUserId();
        const tenantSlug = currentTenantSlug();

        // The tenant slug is required, not incidental: broadcasting/auth is served under
        // `/{slug}/api/v1/`, so subscribing before `setTenant()` has resolved the tenant
        // posts to the `/-/` placeholder prefix and comes back 404 "Tenant not found"
        // (MOVEPRO-CRM-K5). `fetchUser()` populates the user before the tenant, so the
        // watch below can see an id with no slug yet — wait for both.
        if (!userId || !tenantSlug || !window.echo) {
            return;
        }

        const channelName = `App.Models.User.${userId}`;

        try {
            if (echoChannelName.value) {
                cleanupEchoListener();
            }

            const channel = window.echo.private(channelName);
            echoChannelName.value = channelName;

            channel.listen(broadcastEventName, onBroadcastNotification).error((error: any) => {
                console.error('Echo channel error in useStaffAssignmentRealtime:', error);
            });
        } catch (error) {
            console.error('Failed to setup Echo listener in useStaffAssignmentRealtime:', error);
        }
    };

    // Both halves of the channel's identity are watched: the user id names the channel and
    // the tenant slug decides which API prefix authorizes it, so a tenant switch has to
    // re-subscribe just like a user change does.
    watch(
        () => [currentUserId(), currentTenantSlug()],
        ([newId, newSlug], [oldId, oldSlug] = []) => {
            if (newId === oldId && newSlug === oldSlug) {
                return;
            }

            cleanupEchoListener();

            if (newId && newSlug) {
                setupEchoListener();
            }
        },
        { immediate: true },
    );

    const instance = { setupEchoListener };
    activeListener = instance;

    onUnmounted(() => {
        if (activeListener === instance) {
            activeListener = null;
        }

        cleanupEchoListener();
    });

    return {
        cleanupEchoListener,
        setupEchoListener,
        // Exposed for Vitest — handleInbound is normally invoked by Echo, but tests can
        // call it directly with a mocked payload to assert the toast + invalidate path.
        handleInbound,
    };
}
